import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { Consultant } from '../../interfaces/Consultant';
import { dataActionCreators, Project, ProjectAssigment } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { useEditorStyles } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Label, Link, Switch, SwitchOnChangeData, Text, Toast, ToastBody, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { useConsultants, useCustomers, useProjects } from '../Tools/ObjectLoader';

export const ProjectsAssigmentsDeleteConfirm = () => {
    // All data needed for edition should be loaded before calling the editor
    const projectAssigmentDelete = useSelector<ApplicationState, ProjectAssigment | null>((state: ApplicationState) => state.data.projectAssigmentDelete);
    const { customers } = useCustomers();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { projects } = useProjects();
    const { consultants } = useConsultants();
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t, i18n } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Form validation
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    // Dependencies
    const project: Project | undefined = React.useMemo(
        () => {
            return projects && projectAssigmentDelete ? projects.find((c) => c.projectid === projectAssigmentDelete.projectid) : undefined;
        }, [projects, projectAssigmentDelete]);

    const customer: Customer | undefined = React.useMemo(
        () => {
            return customers && project ? customers.find((c) => project && c.customerid === project.customerid) : undefined;
        }, [customers, project]);

    const consultant: Consultant | undefined = React.useMemo(
        () => {
            return consultants && projectAssigmentDelete ? consultants.find((c) => projectAssigmentDelete && c.consultantid === projectAssigmentDelete.consultantid) : undefined;
        }, [consultants, projectAssigmentDelete]);

    if (projectAssigmentDelete === null) {
        return null;
    }

    const submit = () => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                {i18n.t('DeletingProjectAssigment', { ns: "Toasts" })}
                            </ToastTitle>
                            <ToastBody>
                                <div className={styles.Vertical}>
                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                    <Text>{t('Project') + " " + (project ? project.displayName : "?")}</Text>
                                    <Text>{t('ProjectsAssigment') + " " + (projectAssigmentDelete ? projectAssigmentDelete.displayName : "?")}</Text>
                                    <Text>{t('Consultant') + " " + (consultant ? consultant.displayName : "?")}</Text>
                                </div>
                            </ToastBody>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/ProjectsAssigments/" + (projectAssigmentDelete && projectAssigmentDelete.projectAssignmentid !== null ? projectAssigmentDelete.projectAssignmentid : ""), {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('DeletingProjectAssigmentSucceed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{t('Project') + " " + (project ? project.displayName : "?")}</Text>
                                                    <Text>{t('ProjectsAssigment') + " " + (projectAssigmentDelete ? projectAssigmentDelete.displayName : "?")}</Text>
                                                    <Text>{t('Consultant') + " " + (consultant ? consultant.displayName : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                if (projectAssigmentDelete) dispatch(dataActionCreators.patchProjectAssigmentDeletion(projectAssigmentDelete));
                                dispatch(dataActionCreators.requestProjectAssigmentDeletion(null));
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('DeletingProjectAssigmentFailed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{t('Project') + " " + (project ? project.displayName : "?")}</Text>
                                                    <Text>{t('ProjectsAssigment') + " " + (projectAssigmentDelete ? projectAssigmentDelete.displayName : "?")}</Text>
                                                    <Text>{t('Consultant') + " " + (consultant ? consultant.displayName : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>Delete timesheet</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestProjectAssigmentDeletion(null));
                        }}
                        autoComplete="off"
                    >
                        <Field label={t('Consultant')}>
                            <Label className={styles.label}>{consultant && consultant.displayName ? consultant.displayName : "-"}</Label>
                        </Field>
                        <Field label={t('Customer')}>
                            <Label className={styles.label}>{customer && customer.displayName ? customer.displayName : "-"}</Label>
                        </Field>
                        <Field label={t('Project')}>
                            <Label className={styles.label}>{project && project.displayName ? project.displayName : "-"}</Label>
                        </Field>
                        <Field label={t('ProjectsAssigment')} >
                            <Label className={styles.label}>{projectAssigmentDelete && projectAssigmentDelete.displayName ? projectAssigmentDelete.displayName : "-"}</Label>
                        </Field>
                        <Field label="Description">
                            <Label className={styles.label}>{projectAssigmentDelete && projectAssigmentDelete.description ? projectAssigmentDelete.description : ""}</Label>
                        </Field>
                        <Switch checked={confirmDelete} label={t('ConfirmDelete')} onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setConfirmDelete(data.checked); }} />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestProjectAssigmentDeletion(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!confirmDelete}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};